import { LegalDoc } from "@/components/marketing/LegalDoc";

const sections = [
  {
    heading: "Who we are",
    body: [
      "HypeFlow AI (\"HypeFlow\", \"we\", \"us\", or \"our\"), operated by Hypeflow B.V. (Herengracht 320, 1016 CE Amsterdam, The Netherlands), provides an AI-powered creator marketing platform that helps brands and agencies discover, manage, and measure influencer campaigns on TikTok and Instagram.",
      "This Privacy Policy explains what personal information we collect, how we use and protect it, and the choices you have. It applies to our website, marketing pages, and the HypeFlow workspace application (together, the \"Service\").",
      "If you have questions, contact us at support@hypeflow.cc.",
    ],
  },
  {
    heading: "Information we collect",
    body: [
      { sub: "Information you provide" },
      {
        list: [
          "Account details — your name, work email, company name, and password (stored only as a salted hash).",
          "Workspace content — campaigns, notes, saved creators (CRM), outreach drafts, and other data you create in the Service.",
          "Billing information — when you upgrade a plan, payments are processed by Stripe. We receive confirmation and plan details but never see or store your full card number.",
          "Communications — messages you send us via the contact form, support, or email.",
        ],
      },
      { sub: "Information collected automatically" },
      {
        list: [
          "Usage data — pages viewed, features used, search counts, and similar product analytics.",
          "Device and log data — IP address, browser type, and timestamps, used for security, rate limiting, and diagnostics.",
          "Cookies and similar technologies — see the Cookies section below.",
        ],
      },
      { sub: "Creator and social media data" },
      "To power creator discovery and analytics, we process publicly available information about social media creators (such as public follower counts, engagement metrics, and recent public posts) obtained through third-party data providers. Audience demographic estimates shown in the product may be modeled approximations and are clearly labeled as estimates where applicable.",
    ],
  },
  {
    heading: "How we use information",
    body: [
      {
        list: [
          "Provide, operate, and maintain the Service and your workspace.",
          "Authenticate you, secure accounts, enforce plan limits, and prevent abuse.",
          "Process payments and manage subscriptions.",
          "Generate AI-assisted outputs (e.g., campaign briefs, outreach drafts, match analysis) based on data you choose to provide.",
          "Send transactional emails such as password resets and account notices.",
          "Improve the Service, understand usage, and develop new features.",
          "Comply with legal obligations and enforce our Terms of Service.",
        ],
      },
    ],
  },
  {
    heading: "Legal bases for processing",
    body: [
      "Where the GDPR or similar laws apply, we rely on the following legal bases: performance of a contract (to provide the Service you request); our legitimate interests (to secure, improve, and market the Service in a balanced way); your consent (for non-essential cookies and certain communications); and compliance with legal obligations.",
    ],
  },
  {
    heading: "How we share information",
    body: [
      "We do not sell your personal information. We share it only with service providers who process it on our behalf under appropriate agreements, including:",
      {
        list: [
          "Stripe — payment processing.",
          "Resend — delivery of transactional emails.",
          "Social data providers (e.g., Apify) — retrieval of public creator metrics.",
          "Cloud hosting and database infrastructure — to run the Service.",
          "AI model providers — to generate AI-assisted content from the inputs you provide.",
        ],
      },
      "We may also disclose information to comply with the law, enforce our agreements, or protect the rights, safety, and security of our users and the public. If we are involved in a merger or acquisition, information may be transferred as part of that transaction with notice to you.",
    ],
  },
  {
    heading: "Cookies and tracking",
    body: [
      "We use strictly necessary cookies and local storage to keep you signed in and to remember preferences. We may use privacy-respecting analytics to understand aggregate usage. You can control cookies through your browser settings; disabling essential cookies may affect how the Service works.",
    ],
  },
  {
    heading: "Data retention",
    body: [
      "We keep your personal information for as long as your account is active or as needed to provide the Service. We retain certain records (such as billing records) for as long as required by law. When data is no longer needed, we delete or anonymize it. You may request deletion of your account at any time.",
    ],
  },
  {
    heading: "Security",
    body: [
      "We protect your information with industry-standard measures, including encrypted transport (HTTPS), hashed passwords, scoped access controls, and single-use, time-limited tokens for sensitive actions such as password resets. No method of transmission or storage is completely secure, but we work continuously to safeguard your data.",
    ],
  },
  {
    heading: "Your rights and choices",
    body: [
      "Depending on where you live (including under the EU General Data Protection Regulation (GDPR) and the UK GDPR), you may have the right to access, correct, delete, or export your personal information, to object to or restrict certain processing, and to withdraw consent. To exercise these rights, email support@hypeflow.cc and we will respond within the timeframe required by applicable law. You also have the right to lodge a complaint with a supervisory authority, such as the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).",
      "You can delete your account yourself at any time in Account Settings. This permanently removes your workspace data and any Studio credits; payment records are kept only as long as Dutch bookkeeping law requires. Analytics cookies are only used with your consent; see our Cookie Policy to change your choice.",

      "Creators: if your public profile appears in our discovery tools and you would like it reviewed or removed, contact support@hypeflow.cc and we will action reasonable requests.",
    ],
  },
  {
    heading: "International transfers",
    body: [
      "We are based in the European Union and serve customers worldwide. Where personal data is transferred outside the European Economic Area, we use appropriate safeguards such as the European Commission's Standard Contractual Clauses.",
    ],
  },
  {
    heading: "Children's privacy",
    body: [
      "The Service is intended for businesses and is not directed to children. We do not knowingly collect personal information from anyone under 16. If you believe a child has provided us information, contact us and we will delete it.",
    ],
  },
  {
    heading: "Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. When we make material changes, we will update the \"Last updated\" date and, where appropriate, notify you. Your continued use of the Service after changes take effect means you accept the updated policy.",
    ],
  },
  {
    heading: "Contact us",
    body: [
      "For any privacy question or request, email support@hypeflow.cc. We take your privacy seriously and aim to respond promptly.",
      "Data controller: Hypeflow B.V., Herengracht 320, 1016 CE Amsterdam, The Netherlands.",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalDoc
      testid="privacy-page"
      eyebrow="Privacy"
      title="Privacy Policy"
      updated="8 June 2026"
      intro="Your trust matters to us. This policy describes the personal information HypeFlow AI collects, how we use and share it, and the control you have over it."
      sections={sections}
    />
  );
}
